<template>
    <div>
        <div class="label" :style="coloredLabel ? `color: var(--v-${color}-base)` : ''"> {{ label }}</div>
        <v-combobox v-if="canWrite || searchable" :color="color" :item-color="color" v-model="model"
            :items="itemsAvailable" :placeholder="placeholder" :item-text="itemText" :item-value="itemValue"
            disable-lookup :solo="!outlined || solo" :flat="!outlined || solo" :outlined="outlined" :dense="dense"
            :prepend-inner-icon="icon" :clearable="clearable" @click:clear="$emit('clear')" @change="onSelected"
            hide-details :class="['mb-2', { 'kimsa-elevation': !outlined }, { 'placeholder-text': placeholderClass }]"
            :height="height" :return-object="!noObject" :no-data-text="$t('common.not_found')"
            :multiple="multipleWithChips" :readonly="readonly" :search-input.sync="search" @blur="$emit('blur')"
            :loading="loading" ref="select">
            <template v-if="multipleWithChips" v-slot:selection="{ item, index }">
                <div v-if="index === 0" class="text-truncate" style="width: 90%">
                    {{ item.name }}
                    <span v-if="model.length > 1" class="caption">
                        (+{{ model.length - 1 }})
                    </span>
                </div>
            </template>
            <template v-slot:no-data>
                <v-list-item link @click="createNew(search)">
                    <v-list-item-content>
                        <v-list-item-title>
                            {{ canWrite && !noCreateExercise ? $t('routines.no_data_create') : $t('common.not_found') }}
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>
            <template v-if="showVideo || withListIcon" v-slot:item="{ active, item, attrs, on }">
                <v-list-item v-if="showVideo" class="item-pointer">
                    <v-list-item-action @click="onClickItem(item)">
                        <v-img height="40" width="40" :src="thumbImg(item) || imgPlaceholder"
                            :lazy-src="imgPlaceholder"></v-img>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            <v-row no-gutters align="center">
                                <span>{{ item.name }}</span>
                            </v-row>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
                <v-list-item v-if="withListIcon" class="item-pointer">
                    <v-list-item-action>
                        <v-avatar size="20" :color="item.hasInjury ? 'error' : 'accent'">
                            <v-icon color="#FFFFFF" small>{{ item.leftIcon }}</v-icon>
                        </v-avatar>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            <v-row no-gutters align="center">
                                <span>{{ item.name }}</span>
                            </v-row>
                        </v-list-item-title>
                    </v-list-item-content>
                    <v-list-item-action>
                        <v-avatar size="20" :color="item.rightIcon.color">
                            <v-icon color="#FFFFFF" small>{{ item.rightIcon.icon }}</v-icon>
                        </v-avatar>
                    </v-list-item-action>
                </v-list-item>
            </template>
        </v-combobox>
        <v-select v-else :color="color" :item-color="color" v-model="model" :items="itemsAvailable"
            :placeholder="placeholder" :item-text="itemText" :item-value="itemValue" disable-lookup
            :solo="!outlined || solo" :flat="!outlined || solo" :outlined="outlined" :dense="dense"
            :prepend-inner-icon="icon" :clearable="clearable" @click:clear="$emit('clear')" @change="onSelected"
            hide-details :class="['mb-2', { 'kimsa-elevation': !outlined }, { 'placeholder-text': placeholderClass }]"
            :height="height" :return-object="!noObject" :no-data-text="$t('common.not_found')"
            :multiple="multipleWithChips" :readonly="readonly" menu-props="offsetY" ref="select">
            <template v-if="multipleWithChips && showCloseButton" v-slot:append-item>
                <div class="append">
                    <v-list-item link @click="$refs.select.blur()">
                        <v-list-item-action>
                            <v-icon>mdi-close</v-icon>
                        </v-list-item-action>
                        <v-list-item-content>
                            <v-list-item-title>
                                <span>{{ $t('common.close') }}</span>
                            </v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </div>
            </template>
            <template v-if="multipleWithChips" v-slot:selection="{ item, index }">
                <div v-if="index === 0" class="text-truncate" style="width: 90%">
                    {{ item.name }}
                    <span v-if="model.length > 1" class="caption">
                        (+{{ model.length - 1 }})
                    </span>
                </div>
            </template>
            <template v-if="multipleWithChips" v-slot:item="{ active, item, attrs, on }">
                <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
                    <v-list-item-action>
                        <v-checkbox :color="color" :input-value="active"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                        <v-list-item-title>
                            <span>{{ item.name }}</span>
                        </v-list-item-title>
                    </v-list-item-content>
                </v-list-item>
            </template>
        </v-select>

        <div v-if="withInfo" class="mb-2">
            <v-chip color="#CCFF99" @click="remove(item)" class="black--text mr-2 my-1 kimsa-elevation" label
                v-for="item in array" :key="item.value">
                {{ item.name }}
            </v-chip>
        </div>

    </div>
</template>

<script>
export default {
    name: "KimsaCombobox",
    props: {
        color: {
            type: String,
            default: 'secondary'
        },
        label: {
            type: String,
            default: ''
        },
        placeholder: {
            type: String,
            default: ''
        },
        items: {
            type: Array,
            default: []
        },
        multiple: {
            type: Boolean,
            default: false
        },
        withInfo: {
            type: Boolean,
            default: false
        },
        outlined: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        solo: {
            type: Boolean,
            default: false
        },
        placeholderClass: {
            type: Boolean,
            default: false
        },
        multipleWithChips: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        readonly: {
            type: Boolean,
            default: false
        },
        coloredLabel: {
            type: Boolean,
            default: false
        },
        itemText: {
            type: String,
            default: 'name'
        },
        itemValue: {
            type: String,
            default: 'value'
        },
        noObject: {
            type: Boolean,
            default: false
        },
        canWrite: {
            type: Boolean,
            default: false,
        },
        showVideo: {
            type: Boolean,
            default: false,
        },
        value: {
            type: [Number, String, Object, Array]
        },
        icon: {
            type: String,
            default: undefined,
        },
        height: {
            type: [Number, String],
            default: undefined,
        },
        showCloseButton: {
            type: Boolean,
            default: false,
        },
        maxSelected: {
            type: [String, Number],
            default: null,
        },
        searchable: {
            type: Boolean,
            default: false,
        },
        withListIcon: {
            type: Boolean,
            default: false,
        },
        noCreateExercise: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {
        const vm = this
        if (typeof vm.value === 'object') {
            vm.model = JSON.parse(JSON.stringify(vm.value))
        } else {
            vm.model = vm.value
        }
    },
    data() {
        return {
            model: null,
            array: [],
            search: null,
        }
    },
    computed: {
        itemsAvailable(vm = this) {
            if (vm.array.length === 0 && (!vm.multiple || !vm.multipleWithChips)) return vm.items
            if (vm.items.length === 0) return []
            return vm.items.filter(el => !vm.simpleArraySelected.includes(el.name))
        },
        simpleArraySelected(vm = this) {
            if (vm.array.length === 0) return []
            return vm.array.map(item => item.name)
        },
    },
    methods: {
        onSelected() {
            var vm = this

            let currentLength = vm.model && vm.model.length || 0
            if (this.maxSelected && Number(this.maxSelected) < currentLength) {
                vm.model.pop()
                return this.toast(vm.$t('exercises.max_exercises_to_show'), 'error')
            }

            if (!vm.model || !vm.multiple) return

            vm.array.push(vm.model)

            vm.$nextTick(() => {
                vm.model = null
            })
        },
        remove(item) {
            var vm = this

            if (vm.withInfo) {
                vm.array.forEach((el, i) => {
                    if (el.name === item.name) {
                        vm.array.splice(i, 1)
                    }
                })
            }
        },
        setDefaults(array) {
            const vm = this
            vm.array = array.map(item => {
                return {
                    name: item,
                    value: item,
                }
            })
        },
        createNew(val) {
            const vm = this
            if (!vm.canWrite || vm.noCreateExercise) return
            vm.$nextTick(() => {
                vm.search = null
                vm.model = null
                vm.$refs.select.blur()
            })
            vm.$emit('create', val)
        },
        onClickItem(item) {
            const vm = this
            if (!vm.thumbImg(item) || !item.video) return
            vm.$emit('showVideo', item)
        },
        thumbImg(item) {
            return item.img || (item.video && item.video.thumb) || null
        },
        closeMenu() {
            const vm = this
            if (vm.$refs.select) {
                vm.$refs.select.isFocused = false
                vm.$refs.select.isMenuActive = false
            }
        },
        openMenu() {
            const vm = this
            if (vm.$refs.select) {
                vm.$refs.select.isFocused = true
                vm.$refs.select.isMenuActive = true
            }
        },
    },
    watch: {
        value(val) {
            if (typeof value === 'object') {
                this.model = JSON.parse(JSON.stringify(val))
            } else {
                this.model = val
            }
        },
        model(val) {
            if (this.canWrite) {
                if (val && typeof val === 'object') {
                    return this.$emit('change', val)
                } else {
                    return
                }
            }
            if (!this.multiple) {
                this.$emit('change', val)
            }
        },
        array(val) {
            if (this.multiple || this.multipleWithChips) {
                this.$emit('change', val)
            }
        },
        search(val) {
            if (!this.model) this.$emit('search', val)
        }
    }
}
</script>

<style scoped>
.label {
    font-size: 15px;
    color: #707070;
}

.equipment-box {
    border: 1px solid #999999;
    border-radius: 4px;
    padding: 5px
}

.placeholder-text {
    font-size: 13px;
    color: #707070;
}

::v-deep .v-select.v-input--dense .v-select__selection--comma {
    margin: auto !important;
}

.v-select__slot .v-select__selections {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.item-pointer {
    cursor: pointer;
}

.item-pointer:hover {
    background-color: rgba(0, 0, 0, 0.03) !important;
}

.v-list-item--active {
    background-color: rgba(102, 204, 204, 0.1) !important;
}

.v-list-item--active:hover {
    background-color: rgba(102, 204, 204, 0.1) !important;
}

::v-deep .v-select__selections {
    flex-wrap: nowrap;
}

.append {
    position: sticky;
    bottom: 0;
    background: white;
    border-top: 1px solid black;
}
</style>